import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import styles from './EnterpriseSearch.module.scss'

import { EnterpriseListItem } from '@/api/generated'
import { searchEnterprises } from '@/api/internal'
import Input from '@/components/Input'
import { Spinner } from '@/components/Spinner'

const DEBOUNCE_MS = 300

interface EnterpriseSearchProps {
  onSelect: (enterprise: EnterpriseListItem) => void
  onError: (error: string | null) => void
}

const EnterpriseSearch = ({ onSelect, onError }: EnterpriseSearchProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [dismissed, setDismissed] = useState(false)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  // Debounce the search query
  useEffect(() => {
    if (searchQuery.length < 2) {
      setDebouncedQuery('')
      return
    }

    const timeoutId = setTimeout(() => {
      setDebouncedQuery(searchQuery)
      setDismissed(false)
    }, DEBOUNCE_MS)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const {
    data: searchResults = [],
    isFetching,
    isSuccess,
    error,
  } = useQuery({
    queryKey: ['enterpriseSearch', debouncedQuery],
    queryFn: async () => {
      const response = await searchEnterprises(debouncedQuery)
      return response.data.enterprises
    },
    enabled: debouncedQuery.length >= 2,
  })

  useEffect(() => {
    if (error) {
      onError('Failed to search enterprises. Please try again.')
    }
  }, [error, onError])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setDismissed(true)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (enterprise: EnterpriseListItem) => {
    setSearchQuery('')
    setDebouncedQuery('')
    onSelect(enterprise)
  }

  const showResults = isSuccess && !dismissed

  return (
    <div ref={searchContainerRef} className={styles.searchContainer}>
      <label className={styles.label}>Search Enterprise</label>
      <div className={styles.searchInputWrapper}>
        <Input
          type="text"
          className={styles.searchInput}
          placeholder="Type to search enterprises..."
          value={searchQuery}
          onInput={setSearchQuery}
        />
        {isFetching && (
          <div className={styles.searchSpinner}>
            <Spinner size={16} color="gray" />
          </div>
        )}
      </div>
      {showResults && searchResults.length > 0 && (
        <div className={styles.searchResults} role="listbox">
          {searchResults.map((enterprise) => (
            <button
              key={enterprise.id}
              role="option"
              className={styles.searchResultItem}
              onClick={() => handleSelect(enterprise)}
            >
              {enterprise.name}
            </button>
          ))}
        </div>
      )}
      {showResults && searchResults.length === 0 && !isFetching && (
        <div className={styles.searchResults}>
          <div className={styles.noResults}>No enterprises found</div>
        </div>
      )}
    </div>
  )
}

export default EnterpriseSearch
